"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkPurchaseErrorV3Local = exports.LinkPurchaseErrorV3Bound = exports.LinkPurchaseCancelBoundResponse = exports.LinkPurchaseCancelBoundRequest = exports.LinkPurchaseSetOnlineResponseV3 = exports.LinkPurchaseRefusedV3 = exports.LinkPurchaseCalculatedV3 = exports.LinkPurchaseSetOnlineRequestV3 = exports.LinkPurchaseVoucherOutcome = exports.LinkPurchaseVoucher = exports.LinkPurchaseVoucherCode = exports.LINK_PURCHASE_VOUCHER_NOT_VALID_KEY = exports.LINK_PURCHASE_PHASE_FINAL = exports.LinkPurchaseDescriptor = exports.LinkPurchaseDescriptorRequest = exports.LinkCrmPurchaseTuple = exports.LinkCrmExchanges = exports.LinkCrmPurchaseExpectation = exports.LinkCrmPreprocessingId = exports.LinkCrmChannelIncarnation = exports.LinkCrmTarget = exports.LinkCrmCorrelationId = exports.CRM_TARGET_VERSION_V1 = exports.CRM_PURCHASE_KEY_DERIVATION_V1 = exports.LINK_CRM_PURCHASE_CONTRACT_V3 = exports.LINK_CRM_CORRELATION_HEADER = exports.LINK_CRM_PURCHASE_ACCEPT_HEADER = exports.LINK_LOYALTY_PURCHASE_CANCEL_BOUND_PATH = exports.LINK_LOYALTY_PURCHASE_DESCRIPTOR_PATH = exports.SyncCrmPurchase = exports.SYNC_CRM_PURCHASE_EXTENSION = exports.LinkPurchaseError = exports.LinkPurchaseCrmDispatched = exports.LINK_CRM_PURCHASE_CONTRACT_VERSION = exports.LINK_CRM_PURCHASE_CONTRACT_HEADER = exports.LinkPurchaseCancelResponse = exports.LinkPurchaseNotFound = exports.LinkPurchaseCancelled = exports.LinkPurchaseCancelRequest = exports.LinkPurchaseSetOnlineResponse = exports.LINK_CRM_REFUSED_ARTICLE_V1 = exports.LINK_CRM_REFUSED_ARTICLE_HEADER = exports.LinkPurchaseRefused = exports.LINK_CRM_REFUSED_ARTICLE_KEY = exports.LinkPurchaseCalculated = exports.LinkPurchaseSetOnlineRequest = exports.LinkPurchaseProduct = exports.LinkPurchaseBasketId = exports.LINK_LOYALTY_PURCHASE_CANCEL_PATH = exports.LINK_LOYALTY_PURCHASE_SET_ONLINE_PATH = void 0;
exports.SYNC_CRM_PURCHASE_FAILURE_MARKER = exports.SyncCrmPurchaseV2 = exports.LINK_CRM_FINAL_CARRIER_V2 = exports.LINK_CRM_FINAL_CARRIER_HEADER = exports.LinkPurchaseErrorV3 = void 0;
exports.linkCrmAcceptsRefusedArticle = linkCrmAcceptsRefusedArticle;
exports.linkCrmPurchaseContractOf = linkCrmPurchaseContractOf;
exports.linkPurchaseNoAnswerOf = linkPurchaseNoAnswerOf;
exports.linkPurchaseCancelOutcomeOf = linkPurchaseCancelOutcomeOf;
exports.syncCrmPurchaseOf = syncCrmPurchaseOf;
exports.linkCrmPurchaseAcceptsV3 = linkCrmPurchaseAcceptsV3;
exports.linkCrmCorrelationOf = linkCrmCorrelationOf;
exports.syncCrmPurchaseClassOf = syncCrmPurchaseClassOf;
const zod_1 = require("zod");
const loyalty_js_1 = require("./loyalty.js");
/**
 * VL-102 / VL-104 / VL-105 — the two-phase CRM purchase of an open receipt,
 * through the store Link (till → Link → `ip-3` CRM "set online" / "cancel").
 *
 *   POST /loyalty/purchases/set-online   body LinkPurchaseSetOnlineRequest
 *   POST /loyalty/purchases/cancel       body LinkPurchaseCancelRequest
 *
 * Same listener, store-pass guard and `loyalty` scope as `/loyalty/lookup`.
 *
 * ------------------------------------------------------------------
 * THE FLOW
 * ------------------------------------------------------------------
 * 1. The payment window opens: "set online" with no write-off (VL-102 AC1).
 *    The CRM answers what may be written off (`availableBonusesMinor`,
 *    `availableCashMinor`) and whether the card belongs to a contact
 *    (`contactIdPresent`, VL-104).
 * 2. After each tender the cashier applies, "set online" again with the
 *    amounts actually used so far: bonuses, money from the card's account,
 *    cash and bank-card amounts (AC2), and the change put on the card's
 *    money account (`residualMinor`, VL-105). The bank card is always the
 *    last tender (AC3).
 * 3. Commit is NOT a route here. It is the receipt outbox's (RL-587), after
 *    the sale was paid and fiscalised, from the uploaded receipt that carries
 *    [SYNC_CRM_PURCHASE_EXTENSION] (AC4). VL-435 slice 1: the FINAL "set
 *    online" is the till's, before fiscalisation (v3 `phase: 'final'`,
 *    [LINK_PURCHASE_PHASE_FINAL]); the outbox checks the upload against its
 *    answer and only commits. It never sends "set online" after the print.
 * 4. Cancel returns the CRM purchase to nothing (AC5, AC6), or says the CRM
 *    holds none under the key (`notFound`, contract v2).
 *
 * ------------------------------------------------------------------
 * RULES
 * ------------------------------------------------------------------
 * - The purchase key (`PreprocessingId`) is never the till's choice: the Link
 *   derives it from the till's terminal id and `basketId`
 *   (`ip3BasketPurchaseKey`), so a till can only address its own receipts.
 * - "set online" moves no money: the CRM keeps the purchase in temporary
 *   storage and a repeat under the same key replaces it; only the commit
 *   counts. A lost answer is therefore resolved by asking again — on the
 *   cashier's word, never silently — or by the final "set online" before the
 *   commit, which sends what the receipt actually records.
 * - A CRM refusal of the receipt (`CardNotBindToContactForWriteOffBonuses`,
 *   a permanent receipt key, a validation error) is an answer: `200` with
 *   `state: 'refused'` and a stable `errorKey`. Everything else is the usual
 *   loyalty error body (503 unavailable, 502 upstream error, …).
 * - Every amount is in minor units. A paid amount above what was asked is a
 *   502, never clamped.
 *
 * ------------------------------------------------------------------
 * CONTRACT v2 — WHAT THE LINK KNOWS (VL-102 P1, cut 2)
 * ------------------------------------------------------------------
 * The Link reports facts precisely; it decides nothing more than before.
 *
 * - **Version header.** Every answer the purchase routes give carries
 *   [LINK_CRM_PURCHASE_CONTRACT_HEADER]: `2`, including the store-pass
 *   guard's own refusals on these paths. No header: a Link from before P1
 *   (version 1). Versions only add; a till reads `>= 2` as P1.
 * - **Cancel: `cancelled` or `notFound`.** `cancelled` only for the CRM's
 *   documented positive envelope. `notFound` for `CannotFindPurchaseWithId`
 *   with `Status: false` on a 400 or a 2xx: the CRM said it holds no
 *   temporary purchase under the key (never set online, already cancelled,
 *   already committed, or expired: it does not say which). Version 1
 *   answered that as `cancelled`, so a v1 `cancelled` is either.
 *   The same key on a 5xx, or without `Status: false`, is no answer.
 * - **Error bodies say whether the call reached the CRM**
 *   ([LinkPurchaseError], `crmDispatched`), decided where the Link answers:
 *   - `no` — the call never left the Link: loyalty not on this Link,
 *     purchases not enabled, the source changed or the breaker open before
 *     any CRM call, the receipt already paid, the purchase state unreadable,
 *     a body or pass the Link refused, a rate limit. Says nothing about an
 *     EARLIER call for the same receipt.
 *   - `maybe` — the Link entered a CRM call and has no definite answer: a
 *     timeout, a cut connection, a CRM 5xx or non-envelope, an answer the
 *     Link cannot read, a source change after a first exchange, or a fault
 *     the Link cannot place. The CRM may have applied it.
 *   A definite CRM answer is never an error body: `calculated`, `refused`
 *   (set-online) and `cancelled`, `notFound` (cancel) are `200`.
 * - **The statuses are unchanged** (503 stays 503), so a till from before P1
 *   reads every answer as it did, on the safe side:
 *   - 200 `cancelled`, `calculated`, `refused`: byte for byte the v1 bodies;
 *   - 200 `notFound`: not its contract, so malformed, so "may have landed"
 *     (`LinkPurchaseCancelAnswer.fromJson` refuses any state but
 *     `cancelled`) — never read as cancelled;
 *   - error bodies: read by status only; `crmDispatched` is ignored, so a
 *     5xx stays "may have landed".
 * - [linkPurchaseNoAnswerOf] and [linkPurchaseCancelOutcomeOf] are the
 *   reading rule a till applies, written once here.
 */
exports.LINK_LOYALTY_PURCHASE_SET_ONLINE_PATH = '/loyalty/purchases/set-online';
exports.LINK_LOYALTY_PURCHASE_CANCEL_PATH = '/loyalty/purchases/cancel';
const SafeMinor = zod_1.z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
/** The till's basket id (a ULID today): the open receipt the purchase belongs to. */
exports.LinkPurchaseBasketId = zod_1.z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);
/** One live receipt line. Amounts in minor units, before any CRM write-off. */
exports.LinkPurchaseProduct = zod_1.z
    .object({
    /** 1-based over the receipt's live lines; the CRM echoes it as `CashdeskPosition`. */
    position: zod_1.z.number().int().min(1).max(9999),
    code: zod_1.z.string().trim().min(1).max(64),
    /** Units, or kilograms for a weighed line. */
    quantity: zod_1.z.number().positive().max(1_000_000),
    priceMinor: SafeMinor,
    /** What the line costs after the till's own discounts, before the write-off. */
    amountMinor: SafeMinor,
})
    .strict();
/** The fields of a set-online request (v2 and v3 alike). */
const SET_ONLINE_REQUEST_SHAPE = {
    basketId: exports.LinkPurchaseBasketId,
    cardNumber: loyalty_js_1.LoyaltyMemberNumber,
    products: zod_1.z.array(exports.LinkPurchaseProduct).min(1).max(500),
    /** Bonuses used so far (CRM `BonusesToWriteOff`); 0 on the first call. */
    bonusesToWriteOffMinor: SafeMinor,
    /** Money from the card's account used so far (CRM `CashToWriteOff`); 0 on the first call. */
    cashToWriteOffMinor: SafeMinor,
    /** Cash taken so far (CRM `Payments` type `1`). */
    cashPaidMinor: SafeMinor.default(0),
    /** Bank card amount (CRM `Payments` type `0`), sent before the terminal is asked. */
    cardPaidMinor: SafeMinor.default(0),
    /**
     * VL-105: the change — all of it or the part the customer agreed to —
     * put on the card's money account (CRM `ResidualAmount`). 0: none.
     */
    residualMinor: SafeMinor.default(0),
};
function uniquePositions(request, ctx) {
    const seen = new Set();
    for (const product of request.products) {
        if (seen.has(product.position)) {
            ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'duplicate position' });
            return;
        }
        seen.add(product.position);
    }
}
exports.LinkPurchaseSetOnlineRequest = zod_1.z
    .object(SET_ONLINE_REQUEST_SHAPE)
    .strict()
    .superRefine(uniquePositions);
/** `200` of set-online: the CRM calculated the receipt. */
exports.LinkPurchaseCalculated = zod_1.z
    .object({
    basketId: exports.LinkPurchaseBasketId,
    state: zod_1.z.literal('calculated'),
    /** The CRM purchase key this receipt is calculated (and will be committed) under. */
    preprocessingId: zod_1.z.string().uuid(),
    /**
     * VL-104: whether the CRM named a contact for the card (`ContactId`).
     * False — empty, absent or the all-zero GUID: the till allows no bonus
     * and no money write-off and sends both as 0.
     */
    contactIdPresent: zod_1.z.boolean(),
    /**
     * CRM `AvailableBonuses` (the documented misspelling `AvaliableBonuses`
     * only as a fallback); null when the answer carried neither.
     */
    availableBonusesMinor: SafeMinor.nullable(),
    /** CRM `AvaliableCashBonuses`; null when the answer did not carry it. */
    availableCashMinor: SafeMinor.nullable(),
    /** CRM `BonusesPaidAmount`: never above what was asked. */
    bonusesPaidMinor: SafeMinor,
    /** CRM `CashBonusesPaidAmount`: never above what was asked. */
    cashBonusesPaidMinor: SafeMinor,
    /** CRM `FinalCashPaidAmount`, when carried. Display only. */
    finalCashPaidMinor: SafeMinor.nullable(),
})
    .strict();
/** The only refusal key that may name an article (VL-449 S2). */
exports.LINK_CRM_REFUSED_ARTICLE_KEY = 'CannotFindProductWithCode';
/**
 * VL-449 S2 (PC 7-41910): `refusedArticle` travels only with
 * [LINK_CRM_REFUSED_ARTICLE_KEY]; an article under any other key is no answer.
 */
function refusedArticleKeyed(value, ctx) {
    if (value.state === 'refused' &&
        value.refusedArticle !== undefined &&
        value.errorKey !== exports.LINK_CRM_REFUSED_ARTICLE_KEY) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['refusedArticle'],
            message: `refusedArticle only with ${exports.LINK_CRM_REFUSED_ARTICLE_KEY}`,
        });
    }
}
/** `200` of set-online: the CRM refused this receipt as sent (the object, for unions). */
const LinkPurchaseRefusedObject = zod_1.z
    .object({
    basketId: exports.LinkPurchaseBasketId,
    state: zod_1.z.literal('refused'),
    /** The CRM's documented key, or `validation`. Never CRM text. */
    errorKey: zod_1.z
        .string()
        .max(64)
        .regex(/^[A-Za-z][A-Za-z0-9_]*$/),
    /**
     * VL-449 S2: with `CannotFindProductWithCode`, the article the CRM could
     * not find — only to a till that sent [LINK_CRM_REFUSED_ARTICLE_HEADER],
     * and only when it is exactly one `Products[].Code` of that request.
     * Omitted otherwise (never null).
     */
    refusedArticle: zod_1.z
        .string()
        .regex(/^[0-9]{1,20}$/)
        .optional(),
})
    .strict();
/** `200` of set-online: the CRM refused this receipt as sent. */
exports.LinkPurchaseRefused = LinkPurchaseRefusedObject.superRefine(refusedArticleKeyed);
/**
 * VL-449 S2: the request header a till sends on "set online" (v2 and v3) to
 * accept `refusedArticle` on a refused answer. Exactly `1`, once; anything
 * else is no opt-in, and the answer is exactly what it was before.
 */
exports.LINK_CRM_REFUSED_ARTICLE_HEADER = 'x-reform-crm-refused-article';
exports.LINK_CRM_REFUSED_ARTICLE_V1 = '1';
/** Whether a request opted in to `refusedArticle`: the header exactly `1`, once. */
function linkCrmAcceptsRefusedArticle(header) {
    const value = Array.isArray(header) ? (header.length === 1 ? header[0] : undefined) : header;
    return value === exports.LINK_CRM_REFUSED_ARTICLE_V1;
}
exports.LinkPurchaseSetOnlineResponse = zod_1.z
    .discriminatedUnion('state', [exports.LinkPurchaseCalculated, LinkPurchaseRefusedObject])
    .superRefine(refusedArticleKeyed);
exports.LinkPurchaseCancelRequest = zod_1.z.object({ basketId: exports.LinkPurchaseBasketId }).strict();
/** `200` of cancel: the CRM cancelled the temporary purchase under [preprocessingId]. */
exports.LinkPurchaseCancelled = zod_1.z
    .object({
    basketId: exports.LinkPurchaseBasketId,
    preprocessingId: zod_1.z.string().uuid(),
    state: zod_1.z.literal('cancelled'),
})
    .strict();
/**
 * `200` of cancel (contract v2): the CRM holds no temporary purchase under
 * [preprocessingId] (`CannotFindPurchaseWithId`). Not a cancellation: the
 * CRM does not say whether it never had one, cancelled it, committed it or
 * let it expire.
 */
exports.LinkPurchaseNotFound = zod_1.z
    .object({
    basketId: exports.LinkPurchaseBasketId,
    preprocessingId: zod_1.z.string().uuid(),
    state: zod_1.z.literal('notFound'),
})
    .strict();
exports.LinkPurchaseCancelResponse = zod_1.z.discriminatedUnion('state', [
    exports.LinkPurchaseCancelled,
    exports.LinkPurchaseNotFound,
]);
// ------------------------------------------------------------------
// Contract v2 (VL-102 P1): version, crmDispatched, and the reading rule
// ------------------------------------------------------------------
/** The response header naming the purchase routes' contract version. */
exports.LINK_CRM_PURCHASE_CONTRACT_HEADER = 'x-reform-crm-purchase-contract';
/** P1: `notFound` on cancel, `crmDispatched` on every error body. */
exports.LINK_CRM_PURCHASE_CONTRACT_VERSION = 2;
/**
 * The contract version an answer carries: the header's exact positive
 * integer, else 1 (absent, repeated or unreadable — a Link from before P1).
 */
function linkCrmPurchaseContractOf(header) {
    const value = Array.isArray(header) ? (header.length === 1 ? header[0] : undefined) : header;
    if (typeof value !== 'string' || !/^[1-9][0-9]{0,5}$/.test(value))
        return 1;
    return Number(value);
}
/** Whether the call reached the CRM: `no` it never left the Link; `maybe` it may have. */
exports.LinkPurchaseCrmDispatched = zod_1.z.enum(['no', 'maybe']);
/** The purchase routes' error body (v2): the loyalty error body plus `crmDispatched`. */
exports.LinkPurchaseError = zod_1.z
    .object({
    statusCode: zod_1.z.number().int(),
    message: zod_1.z.string().max(200),
    code: zod_1.z.string().max(64),
    crmDispatched: exports.LinkPurchaseCrmDispatched,
})
    .strict();
/**
 * How a till reads a purchase answer that is not `200`:
 * - `notSent` — the call cannot have reached the CRM;
 * - `unknown` — it may have, and the outcome is not known.
 *
 * v2: `notSent` only on a [LinkPurchaseError] whose `statusCode` is the
 * status and whose `crmDispatched` is `no`; anything else is `unknown`.
 * v1 (a Link from before P1): the old rule — a 5xx is `unknown`, any other
 * status `notSent` — and a body's own `crmDispatched` is not trusted.
 */
function linkPurchaseNoAnswerOf(answer) {
    if (answer.contract < exports.LINK_CRM_PURCHASE_CONTRACT_VERSION) {
        return answer.status >= 500 ? 'unknown' : 'notSent';
    }
    const parsed = exports.LinkPurchaseError.safeParse(answer.body);
    return parsed.success &&
        parsed.data.statusCode === answer.status &&
        parsed.data.crmDispatched === 'no'
        ? 'notSent'
        : 'unknown';
}
/**
 * How a till reads a `200` of cancel for [expected.basketId]:
 * - `cancelled` / `notFound` — v2, the CRM's own answer;
 * - `cancelledOrNotFound` — a v1 `cancelled`: v1 answered both so;
 * - `malformed` — not the contract's answer about this receipt (also a
 *   `notFound` without the v2 header: no such Link exists).
 */
function linkPurchaseCancelOutcomeOf(body, expected) {
    const parsed = exports.LinkPurchaseCancelResponse.safeParse(body);
    if (!parsed.success || parsed.data.basketId !== expected.basketId)
        return { kind: 'malformed' };
    const v2 = expected.contract >= exports.LINK_CRM_PURCHASE_CONTRACT_VERSION;
    const { preprocessingId } = parsed.data;
    if (parsed.data.state === 'notFound') {
        return v2 ? { kind: 'notFound', preprocessingId } : { kind: 'malformed' };
    }
    return { kind: v2 ? 'cancelled' : 'cancelledOrNotFound', preprocessingId };
}
// ------------------------------------------------------------------
// The paid sale's record
// ------------------------------------------------------------------
/**
 * `extensions['rl.crmPurchase']` on an uploaded SALE: the two-phase purchase
 * it was paid under.
 *
 * - `basketId` — the purchase key is derived from it and the till, as for the
 *   routes above, so the commit names the purchase the write-off was
 *   calculated under.
 * - `bonusesWrittenOffMinor` / `cashWrittenOffMinor` — what the CRM confirmed
 *   and the till booked; the final "set online" sends exactly these.
 * - `residualMinor` / `residualPosition` — VL-105: the change put on the
 *   card's money account, and the receipt line that books it.
 * - `lines` — the write-off's share per receipt position (1-based over
 *   `lines`), as booked on the line as a discount. The Link adds it back to
 *   the line's amount in the final "set online", so the CRM applies the
 *   write-off once, not on top of an already reduced line.
 */
exports.SYNC_CRM_PURCHASE_EXTENSION = 'rl.crmPurchase';
/** The money facts every `rl.crmPurchase` version carries, with the same rules. */
const SYNC_CRM_PURCHASE_MONEY = {
    bonusesWrittenOffMinor: SafeMinor,
    cashWrittenOffMinor: SafeMinor,
    /** VL-105: the change put on the card's money account (CRM `ResidualAmount`). */
    residualMinor: SafeMinor.default(0),
    /**
     * VL-105: the record line (1-based) that books [residualMinor] on the
     * receipt — the till's change-to-card line. It is not a product the CRM
     * knows, so the final "set online" leaves it out of `Products`.
     */
    residualPosition: zod_1.z.number().int().min(1).max(9999).optional(),
    lines: zod_1.z
        .array(zod_1.z
        .object({
        position: zod_1.z.number().int().min(1).max(9999),
        discountMinor: SafeMinor,
    })
        .strict())
        .max(500),
};
function refineSyncCrmPurchaseMoney(value, ctx) {
    const seen = new Set();
    for (const line of value.lines) {
        if (seen.has(line.position)) {
            ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'duplicate position' });
            return;
        }
        seen.add(line.position);
    }
    // Zod runs this refinement even when a member already failed its own
    // check (a fraction fails `.int()` but stays in the value). Such a member
    // keeps its child issue and is never converted: the total is checked only
    // over members inside the safe-integer domain.
    const members = [
        value.bonusesWrittenOffMinor,
        value.cashWrittenOffMinor,
        ...value.lines.map((line) => line.discountMinor),
    ];
    if (!members.every((member) => Number.isSafeInteger(member)))
        return;
    // Exact: each member is a safe integer, but their sums need not be, and two
    // different sums can round to the same double. BigInt adds them exactly.
    let total = 0n;
    for (const line of value.lines)
        total += BigInt(line.discountMinor);
    if (total !== BigInt(value.bonusesWrittenOffMinor) + BigInt(value.cashWrittenOffMinor)) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'line shares do not add up' });
    }
    if (value.residualMinor > 0 !== (value.residualPosition !== undefined)) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'residual without its line' });
    }
    if (value.residualPosition !== undefined && seen.has(value.residualPosition)) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'write-off on the residual line' });
    }
}
exports.SyncCrmPurchase = zod_1.z
    .object({
    v: zod_1.z.literal(1),
    basketId: exports.LinkPurchaseBasketId,
    ...SYNC_CRM_PURCHASE_MONEY,
})
    .strict()
    .superRefine(refineSyncCrmPurchaseMoney);
/**
 * The record's two-phase purchase: `absent` (no extension — every sale
 * before this, and every sale not paid this way), `invalid` (present but not
 * readable: never sent as an ordinary sale), or the purchase.
 */
function syncCrmPurchaseOf(record) {
    if (record.receiptType !== 'sale')
        return { kind: 'absent' };
    const raw = record.extensions?.[exports.SYNC_CRM_PURCHASE_EXTENSION];
    if (raw === undefined)
        return { kind: 'absent' };
    const found = syncCrmPurchaseClassOf(raw);
    return found.kind === 'v1' || found.kind === 'v2'
        ? { kind: 'purchase', purchase: found.purchase }
        : { kind: 'invalid' };
}
// ------------------------------------------------------------------
// Contract v3 (VL-102 / VL-108 cut 3, design r7 + cr2 verdict 8 C1-C4)
// ------------------------------------------------------------------
/**
 * Contract v3 — the identity binding. PRODUCTION-DISABLED: no till asks for
 * it while `varus.crm.twoPhasePurchase` is off, and cut 3 releases nothing.
 *
 *   POST /loyalty/purchases/descriptor   body LinkPurchaseDescriptorRequest   (v3 only)
 *   POST /loyalty/purchases/set-online   body LinkPurchaseSetOnlineRequestV3  (when accept is 3)
 *   POST /loyalty/purchases/cancel-bound body LinkPurchaseCancelBoundRequest  (v3 only)
 *
 * - **Negotiation.** A till asks for v3 with [LINK_CRM_PURCHASE_ACCEPT_HEADER]
 *   exactly `3` (one value) and names its own id in
 *   [LINK_CRM_CORRELATION_HEADER]. Only then the Link answers v3 bodies with
 *   the contract header `3`. Without it the Link answers exactly v2: an old
 *   till's bytes are unchanged. The v3-only routes refuse a request without
 *   accept 3 (400, `crmDispatched: 'no'`); the v2 `cancel` route refuses a
 *   request WITH it (a v3 till has no unbound Cancel). There is no fallback
 *   from one route or version to another.
 * - **The descriptor** is answered from the Link's local state only: zero
 *   CRM calls, no breaker admission. It names the key derivation, the key the
 *   Link will use for this till and basket, the Creatio target the Link is
 *   configured for, and an opaque channel incarnation (cr2 C1): a new value
 *   whenever the Link's CRM source starts, restarts or is reconfigured.
 * - **The expectation.** A v3 set-online and cancel-bound must carry
 *   `expected` — the descriptor's key, target and incarnation. Before EACH
 *   CRM call (the first set-online, the zero-write-off replacement, the
 *   Cancel) the Link re-checks, with no await between the check and the
 *   network write, that its source is the exact client object it admitted,
 *   that its incarnation, target and key are the expected ones. Otherwise it
 *   refuses: `crmDispatched: 'no'` only when THIS request entered no CRM call.
 * - **The common tuple** ([LinkCrmPurchaseTuple]) is on every v3 answer about
 *   a purchase: the key and target the Link acted under, the correlation id
 *   and how many CRM exchanges this request entered. A till verifies it
 *   against its own binding; an answer never creates a binding.
 * - **Errors** come in two strict variants ([LinkPurchaseErrorV3]): `bound`
 *   carries the tuple; `local` (a refusal before the Link knew a live
 *   identity: a guard, scope, rate limit, body or negotiation refusal, no
 *   channel) carries none and is always `crmDispatched: 'no'`,
 *   `crmExchanges: 0`. `crmDispatched` is `no` exactly when `crmExchanges`
 *   is 0.
 */
exports.LINK_LOYALTY_PURCHASE_DESCRIPTOR_PATH = '/loyalty/purchases/descriptor';
exports.LINK_LOYALTY_PURCHASE_CANCEL_BOUND_PATH = '/loyalty/purchases/cancel-bound';
/** The request header a v3 till asks for the v3 contract with: exactly `3`. */
exports.LINK_CRM_PURCHASE_ACCEPT_HEADER = 'x-reform-crm-purchase-accept';
/** The request header naming the till's own id for this request (op or descriptor id). */
exports.LINK_CRM_CORRELATION_HEADER = 'x-reform-crm-correlation';
/** Contract v3: the identity binding (cut 3). */
exports.LINK_CRM_PURCHASE_CONTRACT_V3 = 3;
/** The one key derivation v3 knows (stored by the till under the same name). */
exports.CRM_PURCHASE_KEY_DERIVATION_V1 = 'crmPurchaseKey/v1';
/** The one target identity v3 knows (`crmTarget/v1`, see `crmTargetV1`). */
exports.CRM_TARGET_VERSION_V1 = 'crmTarget/v1';
/** A till's own request id: the op id or descriptor id it wrote ahead. */
exports.LinkCrmCorrelationId = zod_1.z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);
/** `crmTarget/v1`: lowercase hex SHA-256. */
exports.LinkCrmTarget = zod_1.z.string().regex(/^[0-9a-f]{64}$/);
/** cr2 C1: an opaque handle of the Link's running CRM source (lowercase hex, 128 bits). */
exports.LinkCrmChannelIncarnation = zod_1.z.string().regex(/^[0-9a-f]{32}$/);
/** A lowercase RFC 9562 UUID (`crmPurchaseKey/v1` output). */
exports.LinkCrmPreprocessingId = zod_1.z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/);
/** What a v3 till expects the Link to act under: its binding, exactly. */
exports.LinkCrmPurchaseExpectation = zod_1.z
    .object({
    keyDerivation: zod_1.z.literal(exports.CRM_PURCHASE_KEY_DERIVATION_V1),
    preprocessingId: exports.LinkCrmPreprocessingId,
    crmTargetVersion: zod_1.z.literal(exports.CRM_TARGET_VERSION_V1),
    crmTarget: exports.LinkCrmTarget,
    channelIncarnation: exports.LinkCrmChannelIncarnation,
})
    .strict();
/** The binding fields every v3 answer about a purchase carries. */
const BINDING_SHAPE = {
    basketId: exports.LinkPurchaseBasketId,
    keyDerivation: zod_1.z.literal(exports.CRM_PURCHASE_KEY_DERIVATION_V1),
    preprocessingId: exports.LinkCrmPreprocessingId,
    crmTargetVersion: zod_1.z.literal(exports.CRM_TARGET_VERSION_V1),
    crmTarget: exports.LinkCrmTarget,
    channelIncarnation: exports.LinkCrmChannelIncarnation,
};
/** CRM exchanges this request entered: 0 (descriptor, refused before), 1, or 2 (zero-retry). */
exports.LinkCrmExchanges = zod_1.z.union([zod_1.z.literal(0), zod_1.z.literal(1), zod_1.z.literal(2)]);
/** `T` (r7 §3.2): the common key / target tuple, correlation and exchange count. */
exports.LinkCrmPurchaseTuple = zod_1.z
    .object({
    ...BINDING_SHAPE,
    correlationId: exports.LinkCrmCorrelationId,
    crmExchanges: exports.LinkCrmExchanges,
})
    .strict();
exports.LinkPurchaseDescriptorRequest = zod_1.z.object({ basketId: exports.LinkPurchaseBasketId }).strict();
/** `200` of the descriptor: [LinkCrmPurchaseTuple] with zero exchanges. */
exports.LinkPurchaseDescriptor = zod_1.z
    .object({
    ...BINDING_SHAPE,
    correlationId: exports.LinkCrmCorrelationId,
    crmExchanges: zod_1.z.literal(0),
})
    .strict();
/**
 * VL-435 slice 1 (LAB ONLY, behind the same switches as every purchase
 * call): the `phase` of a v3 set-online that is the receipt's FINAL "set
 * online" — sent by the till after the tenders are chosen and BEFORE the
 * receipt is fiscalised, with the purchase's issued id and the `Payments`
 * (Varus, 2026-10-23, Q-tzyj3 #1: the receipt is fiscalised at the amounts of
 * the last "set online"; after the print only Commit follows).
 *
 * - The Link never opens a purchase on it: a basket with no answered,
 *   owned Creatio id is refused before any CRM call (`crmDispatched: 'no'`).
 * - The Link keeps the answer (the settlement) before answering the till;
 *   any later "set online" of the basket drops it. The paid sale's upload is
 *   then only checked against it and committed; it is never set online
 *   again.
 * - Absent: an ordinary in-sale "set online", exactly as before.
 *
 * A Link from before this answers a body with `phase` as a refused body
 * (400, `crmDispatched: 'no'`): the till then fiscalises no CRM amount.
 */
exports.LINK_PURCHASE_PHASE_FINAL = 'final';
// ------------------------------------------------------------------
// VL-26 successor: the Creatio voucher on the ledgered purchase (plan §9)
// ------------------------------------------------------------------
/**
 * VL-26 successor (DORMANT: the Link refuses every body that uses it unless
 * its `crmPurchaseVouchers` switch is on — `LINK_CRM_PURCHASE_VOUCHERS`,
 * default off — and the till sends none while `varus.crm.twoPhasePurchase`
 * is off).
 *
 * - **Request.** A v3 set-online may carry `vouchers`: at most one
 *   [LinkPurchaseVoucher], on EVERY call of a purchase that has one (Varus
 *   Q-V3: a "set online" without `Vouchers` removes it). Absent: no voucher.
 *   Never on the v2 (unbound) body. The card is optional only with a voucher
 *   (D-2, Q-V4): a cardless body has no `cardNumber` key at all, and the Link
 *   sends Creatio `CardNumber: ""`.
 * - **Answer.** A `calculated` answer to a call that carried `vouchers` has
 *   `voucher` ([LinkPurchaseVoucherOutcome]); absent otherwise.
 * - **Refusal.** Creatio's `400 Voucher is not valid` (the whole call
 *   refused, no `Key`: a single-use voucher held by another open purchase)
 *   is `200 refused` with [LINK_PURCHASE_VOUCHER_NOT_VALID_KEY]. The till
 *   tells the cashier and sends the receipt again without the voucher.
 */
exports.LINK_PURCHASE_VOUCHER_NOT_VALID_KEY = 'voucherNotValid';
/**
 * Creatio's `VoucherCode` (from `GET /api/Voucher/by/{number}`), NOT the
 * printed number: Creatio refuses the number (`CannotFindVoucherWithCode`).
 * A string end to end: it may start with zeros.
 */
exports.LinkPurchaseVoucherCode = zod_1.z.string().regex(/^[0-9A-Za-z_-]{1,64}$/);
/** One voucher of a v3 set-online. */
exports.LinkPurchaseVoucher = zod_1.z
    .object({
    code: exports.LinkPurchaseVoucherCode,
    /** `ApplyPromotionId` of the same `GET`: the Link finds the voucher's line entries by it. */
    applyPromotionId: zod_1.z.string().uuid(),
})
    .strict();
const VoucherPosition = zod_1.z.number().int().min(1).max(9999);
/**
 * What a calculated answer says of the voucher the call carried, reduced by
 * the Link from Creatio's answer (the till never sees `AppliedPromotions`).
 *
 * - `used`: Creatio's `Vouchers[].UsedVouchers` for that code — the only
 *   proof the voucher applied. No such entry: `false`.
 * - `errorKey`: `Vouchers[].ErrorCode` when not used (a code, never text).
 * - `lines`: the voucher's share per position where it is the line's ONLY
 *   `Знижка` promotion: `DiscountAmount − CashdeskDiscount`, minor units.
 * - `mixedPositions`: where it shares the line with another discount, or its
 *   share cannot be read: the voucher's part cannot be told apart, and the
 *   line's whole discount is never reported as the voucher's. The till holds
 *   such a receipt (owner decision D-1).
 *
 * A position is in one list at most; a voucher not used has neither.
 */
exports.LinkPurchaseVoucherOutcome = zod_1.z
    .object({
    code: exports.LinkPurchaseVoucherCode,
    used: zod_1.z.boolean(),
    errorKey: zod_1.z
        .string()
        .regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/)
        .optional(),
    lines: zod_1.z
        .array(zod_1.z.object({ position: VoucherPosition, shareMinor: SafeMinor.min(1) }).strict())
        .max(500),
    mixedPositions: zod_1.z.array(VoucherPosition).max(500),
})
    .strict()
    .superRefine((outcome, ctx) => {
    const seen = new Set();
    for (const position of [
        ...outcome.lines.map((line) => line.position),
        ...outcome.mixedPositions,
    ]) {
        if (seen.has(position)) {
            ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'duplicate position' });
            return;
        }
        seen.add(position);
    }
    if (!outcome.used && seen.size > 0) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'a voucher not used discounts nothing',
        });
    }
});
/**
 * v3 set-online: the v2 fields plus the REQUIRED expectation (and VL-435's
 * optional `phase`). VL-26 successor: optional `vouchers`, and then an
 * optional card (a card OR a voucher, never neither).
 */
exports.LinkPurchaseSetOnlineRequestV3 = zod_1.z
    .object({
    ...SET_ONLINE_REQUEST_SHAPE,
    cardNumber: loyalty_js_1.LoyaltyMemberNumber.optional(),
    vouchers: zod_1.z.array(exports.LinkPurchaseVoucher).min(1).max(1).optional(),
    expected: exports.LinkCrmPurchaseExpectation,
    phase: zod_1.z.literal(exports.LINK_PURCHASE_PHASE_FINAL).optional(),
})
    .strict()
    .superRefine((request, ctx) => {
    uniquePositions(request, ctx);
    if (request.cardNumber === undefined && request.vouchers === undefined) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'a card or a voucher' });
    }
});
const ANSWERED_EXCHANGES = zod_1.z.union([zod_1.z.literal(1), zod_1.z.literal(2)]);
/** v3 `200` calculated: the v2 body and the tuple (one shared `preprocessingId`), and the voucher outcome. */
exports.LinkPurchaseCalculatedV3 = exports.LinkPurchaseCalculated.extend({
    ...BINDING_SHAPE,
    correlationId: exports.LinkCrmCorrelationId,
    crmExchanges: ANSWERED_EXCHANGES,
    /** VL-26 successor: only on an answer to a call that carried `vouchers`. */
    voucher: exports.LinkPurchaseVoucherOutcome.optional(),
}).strict();
/** v3 `200` refused: the v2 body and the tuple (r7 §1.3: v2's refusal had no key). */
const LinkPurchaseRefusedV3Object = LinkPurchaseRefusedObject.extend({
    ...BINDING_SHAPE,
    correlationId: exports.LinkCrmCorrelationId,
    crmExchanges: ANSWERED_EXCHANGES,
}).strict();
exports.LinkPurchaseRefusedV3 = LinkPurchaseRefusedV3Object.superRefine(refusedArticleKeyed);
exports.LinkPurchaseSetOnlineResponseV3 = zod_1.z
    .discriminatedUnion('state', [exports.LinkPurchaseCalculatedV3, LinkPurchaseRefusedV3Object])
    .superRefine(refusedArticleKeyed);
exports.LinkPurchaseCancelBoundRequest = zod_1.z
    .object({ basketId: exports.LinkPurchaseBasketId, expected: exports.LinkCrmPurchaseExpectation })
    .strict();
/** `200` of cancel-bound: `cancelled` or `notFound` (never the same), and the tuple. */
exports.LinkPurchaseCancelBoundResponse = zod_1.z
    .object({
    state: zod_1.z.enum(['cancelled', 'notFound']),
    ...BINDING_SHAPE,
    correlationId: exports.LinkCrmCorrelationId,
    crmExchanges: zod_1.z.literal(1),
})
    .strict();
/** A stable error code: never CRM text. */
const ErrorKey = zod_1.z
    .string()
    .max(64)
    .regex(/^[A-Za-z][A-Za-z0-9_]*$/);
/**
 * The v3 error body, two strict variants (cr2 C4).
 * - `bound`: the Link knew the live identity; the tuple is the one it acted
 *   under. `crmDispatched` is `no` exactly when `crmExchanges` is 0.
 * - `local`: refused before any identity (guard, scope, rate limit, body,
 *   negotiation, no channel). Always `no` and 0; `correlationId` is the
 *   request's header when it was readable, else null. Never binds.
 */
exports.LinkPurchaseErrorV3Bound = zod_1.z
    .object({
    variant: zod_1.z.literal('bound'),
    statusCode: zod_1.z.number().int(),
    message: zod_1.z.string().max(200),
    code: zod_1.z.string().max(64),
    errorKey: ErrorKey,
    crmDispatched: exports.LinkPurchaseCrmDispatched,
    ...BINDING_SHAPE,
    correlationId: exports.LinkCrmCorrelationId,
    crmExchanges: exports.LinkCrmExchanges,
})
    .strict()
    .refine((body) => (body.crmDispatched === 'no') === (body.crmExchanges === 0), {
    message: 'crmDispatched disagrees with crmExchanges',
});
exports.LinkPurchaseErrorV3Local = zod_1.z
    .object({
    variant: zod_1.z.literal('local'),
    statusCode: zod_1.z.number().int(),
    message: zod_1.z.string().max(200),
    code: zod_1.z.string().max(64),
    errorKey: ErrorKey,
    crmDispatched: zod_1.z.literal('no'),
    correlationId: exports.LinkCrmCorrelationId.nullable(),
    crmExchanges: zod_1.z.literal(0),
})
    .strict();
exports.LinkPurchaseErrorV3 = zod_1.z.union([exports.LinkPurchaseErrorV3Bound, exports.LinkPurchaseErrorV3Local]);
/**
 * Whether a request asks for v3: the accept header is exactly `3`, once.
 * Anything else (absent, repeated, `3, 3`, ` 3`, `4`) is not v3: the Link
 * answers the v2 contract, and its v3-only routes refuse.
 */
function linkCrmPurchaseAcceptsV3(header) {
    const value = Array.isArray(header) ? (header.length === 1 ? header[0] : undefined) : header;
    return value === String(exports.LINK_CRM_PURCHASE_CONTRACT_V3);
}
/** The request's correlation id, or null when absent, repeated or not one. */
function linkCrmCorrelationOf(header) {
    const value = Array.isArray(header) ? (header.length === 1 ? header[0] : undefined) : header;
    return exports.LinkCrmCorrelationId.safeParse(value).success ? value : null;
}
// ------------------------------------------------------------------
// Cut 3, item 2: the `rl.crmPurchase` v2 carrier (design r1 §3, r2 §1.5, r3 §3)
// ------------------------------------------------------------------
/**
 * The response header a carrier Link adds to the descriptor `200`: it reads
 * `rl.crmPurchase` v2 on the paid sale's record. A till opens no purchase
 * without exactly this value on the same response.
 */
exports.LINK_CRM_FINAL_CARRIER_HEADER = 'x-reform-crm-final-carrier';
exports.LINK_CRM_FINAL_CARRIER_V2 = '2';
/**
 * `rl.crmPurchase` v2: the v1 money facts plus the binding the till opened
 * the purchase under (the descriptor's key, target and incarnation), the
 * descriptor id and the SHA-256 of the till's stored provenance. The last two
 * are evidence only; the Link has no descriptor journal to check them.
 */
exports.SyncCrmPurchaseV2 = zod_1.z
    .object({
    v: zod_1.z.literal(2),
    basketId: exports.LinkPurchaseBasketId,
    binding: exports.LinkCrmPurchaseExpectation,
    descriptorId: exports.LinkCrmCorrelationId,
    provenanceSha256: zod_1.z.string().regex(/^[0-9a-f]{64}$/),
    ...SYNC_CRM_PURCHASE_MONEY,
})
    .strict()
    .superRefine(refineSyncCrmPurchaseMoney);
/**
 * The failure marker a carrier till sends instead of a purchase it knows of
 * but cannot attach (r3 §3). It has no `v`, and it is never a purchase.
 */
exports.SYNC_CRM_PURCHASE_FAILURE_MARKER = 'rl.crmPurchase.attachmentFailed/1';
/**
 * The raw `rl.crmPurchase` value, classified once and totally: `absent`
 * (undefined), `v1`, `v2`, the failure `marker`, or `unreadable` (anything
 * else that is present: null, a scalar, an unknown version, a body that
 * does not parse). Presence is never read as absence.
 */
function syncCrmPurchaseClassOf(raw) {
    if (raw === undefined)
        return { kind: 'absent' };
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
        return { kind: 'unreadable' };
    }
    const body = raw;
    if (body.failure === exports.SYNC_CRM_PURCHASE_FAILURE_MARKER && !('v' in body)) {
        return { kind: 'marker' };
    }
    if (body.v === 1) {
        const parsed = exports.SyncCrmPurchase.safeParse(raw);
        return parsed.success ? { kind: 'v1', purchase: parsed.data } : { kind: 'unreadable' };
    }
    if (body.v === 2) {
        const parsed = exports.SyncCrmPurchaseV2.safeParse(raw);
        return parsed.success ? { kind: 'v2', purchase: parsed.data } : { kind: 'unreadable' };
    }
    return { kind: 'unreadable' };
}
//# sourceMappingURL=link-crm-purchase.js.map